<template>
  <FilterBadge v-bind="{ ...$props, ...$attrs }">
    <i class="fas fa-user mr-1" />
    {{ text }}
  </FilterBadge>
</template>

<script setup lang="ts">
import { gql } from "@apollo/client/core";
import { computed, onMounted, ref, watch, type PropType } from "vue";
import { apolloClient } from "@/graphql/apollo";
import apolloCache from "@/graphql/apolloCache";
import CreatorFilter from "../../filterImplementations/CreatorFilter";
import FilterBadge from "./FilterBadge.vue";
import joinList from "./joinList";

const props = defineProps({
  filter: {
    type: Object as PropType<CreatorFilter>,
    required: true,
  },
});

const loadedUserNames = ref<Record<number, string>>({});

function readCachedUserName(userId: number) {
  return apolloCache.readFragment<{ preferredName: string }>({
    id: `User:${userId}`,
    fragment: gql`
      fragment SQDCFilterBadgeUserName on User {
        preferredName
      }
    `,
  })?.preferredName;
}

async function loadMissingUsers() {
  const missingUserIds = props.filter.userIds.filter(
    (userId) => readCachedUserName(userId) == null && loadedUserNames.value[userId] == null,
  );
  if (missingUserIds.length === 0) {
    return;
  }

  const { data } = await apolloClient.query<{
    Users: { id: number; preferredName: string }[];
  }>({
    query: gql`
      query SQDCFilterBadgeUsers($ids: [Int!]!) {
        Users(ids: $ids) {
          id
          preferredName
        }
      }
    `,
    variables: {
      ids: missingUserIds,
    },
    fetchPolicy: "cache-first",
  });

  loadedUserNames.value = {
    ...loadedUserNames.value,
    ...Object.fromEntries(data.Users.map((user) => [user.id, user.preferredName])),
  };
}

const text = computed(() => {
  const usernames = props.filter.userIds.map((userId) => {
    return readCachedUserName(userId) ?? loadedUserNames.value[userId] ?? `${userId}`;
  });
  return joinList(usernames);
});

onMounted(loadMissingUsers);
watch(() => props.filter.userIds, loadMissingUsers);
</script>

<style lang="scss" scoped></style>
